import React, { useCallback, useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { Form } from '@components/form/Form/Form';
import './entity-form.scss';
import { useToastContext } from '@context/toastContext';
import FieldCreator from './FieldCreator';
import { useTheme } from '@context/themeContext';
import { getFormDataObj } from '@utils/utils';
import { FormDataObj } from '@utils/types';
import { DeleteIcon } from '@components/icons/icons/DeleteIcon';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal';
import { getVisibleFieldsByFormChange } from '../model/useEntityLoader';
import Tooltip from '@mui/material/Tooltip';
import { FormFileFieldProps } from '@components/form/FileField/FileField';
import { EntityFormProps, EntityMetadata } from '../types';
import { correctFormDataObjBeforeRequest } from '../utils';

const EntityForm = ({
    loading,
    initialEntity,
    entity,
    metadata,
    changeFormFn,
    resetFormFn,
    saveFn,
    deleteFn,
    showDeleteBtn = true,
    deleteTooltip,
    deleteModalTitle,
    icons,
    disabledResetBtn,
    acceptedFileExt,
    ...props
}: EntityFormProps) => {
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const { addToast } = useToastContext();
    const { t } = useTranslation();
    const { theme } = useTheme();

    useEffect(() => {
        // setTimeout helps to disable Save btn on Reset form btn click
        setTimeout(
            () => setFormDisabled(!!formRef.current && !formRef.current.checkValidity()),
            100
        );
    });

    const onFormSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            const { currentTarget } = event;

            const formDataEntryObj = getFormDataObj(event);
            const { visibleMetadataByName } = metadata ?? {};
            const formDataObj = correctFormDataObjBeforeRequest(
                formDataEntryObj,
                visibleMetadataByName ?? {}
            );

            if (!currentTarget.reportValidity()) {
                addToast(`${t('common.not-all-required-fields-are-filled')}`, 'error');
                return;
            }

            setFormDisabled(true);
            await saveFn(formDataObj);
            setFormDisabled(false);
        },
        [metadata, saveFn, addToast, t]
    );

    const formChangeHandler = debounce(
        useCallback(
            async event => {
                const { target, nativeEvent } = event;

                // event.currentTarget can't be used, because it disappeared in debounce
                event.currentTarget = formRef.current;

                setFormDisabled(!formRef.current?.checkValidity());

                const formDataObj = getFormDataObj(event);

                const { visibleMetadataByName } = metadata ?? {};

                const obj = getVisibleFieldsByFormChange({
                    fieldName: nativeEvent.target.name,
                    visibleMetadataByName: visibleMetadataByName ?? {},
                    formDataObj,
                });

                changeFormFn?.(obj as FormDataObj);
            },
            [changeFormFn, metadata]
        ),
        300
    );

    const getFormFields = ({
        metadata,
        entity,
        initialEntity,
        acceptedFileExt,
    }: {
        metadata: EntityMetadata;
        entity: any;
        initialEntity?: Record<string, unknown>;
        acceptedFileExt?: FormFileFieldProps['acceptedFileExt'];
    }) => {
        const { visibleMetadataByName, orderedNames } = metadata;

        return orderedNames.map((name, ind) => {
            if (visibleMetadataByName[name]) {
                const { mandatoryIf } = visibleMetadataByName[name];
                const required = mandatoryIf.apply(entity);

                return (
                    <React.Fragment key={name}>
                        {Object.hasOwn(entity, name) ? (
                            <FieldCreator
                                initialValue={initialEntity?.[name] ?? ''}
                                value={entity[name]}
                                required={required}
                                metadata={visibleMetadataByName[name]}
                                autoFocus={ind === 0}
                                toShowField={Object.hasOwn(entity, name)}
                                acceptedFileExt={acceptedFileExt}
                                // ref={el => (fieldsRefs.current[name] = el)}
                            />
                        ) : null}
                    </React.Fragment>
                );
            }
        });
    };

    const deleteEntityHandler = useCallback(async (): Promise<void> => {
        await deleteFn?.();
    }, [deleteFn]);

    return (
        <div className={`entity-editor ${theme}`} data-testid="entityEditor">
            <div className="entity-editor__header">
                <div className="title">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {loading ? null : metadata ? entity?.name : t('map.no-editable-fields')}
                    </Typography>
                </div>
                <div className="actions">
                    {icons ? icons : null}
                    {showDeleteBtn && deleteFn ? (
                        <Tooltip title={deleteTooltip} placement="top">
                            <IconButton
                                icon={DeleteIcon}
                                width={20}
                                height={20}
                                onClick={() => setShowDeleteConfirmation(true)}
                                borderless
                                testId="entityFormDeleteBtn"
                            />
                        </Tooltip>
                    ) : null}
                    {/*{props.summaryIcon?.(entity?.id)}*/}
                </div>
            </div>
            {metadata ? (
                <Form
                    name="entityForm"
                    onSubmit={onFormSubmit}
                    onChange={formChangeHandler}
                    resetFormFn={resetFormFn}
                    submitBtnTitle={t('common.save')}
                    disabled={formDisabled}
                    disabledResetBtn={disabledResetBtn}
                    ref={formRef}
                    submitBtnTestId="entityFormSubmitBtn"
                    testId="entityForm"
                >
                    {getFormFields({ metadata: metadata, entity, initialEntity, acceptedFileExt })}
                </Form>
            ) : null}
            {showDeleteConfirmation ? (
                <ConfirmationModal
                    title={deleteModalTitle}
                    setModalShow={setShowDeleteConfirmation}
                    onConfirmFn={deleteEntityHandler}
                />
            ) : null}
        </div>
    );
};

export default React.memo(EntityForm);
